import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PostCard = ({ post, onLike, onComment, onShare, onOpenDetails }) => {
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(post?.isLiked || false);
  const [likeCount, setLikeCount] = useState(post?.likes || 0);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const handleLikeClick = () => {
    const newLikedState = !isLiked;
    setIsLiked(newLikedState);
    setLikeCount(prev => newLikedState ? prev + 1 : prev - 1);
    onLike(post?.id, newLikedState);
  };

  const handleProfileClick = () => {
    navigate('/user-profile', { state: { userId: post?.userId } });
  };

  const handleImageClick = () => {
    onOpenDetails(post);
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const postDate = new Date(timestamp);
    const diffInSeconds = Math.floor((now - postDate) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return postDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <article className="bg-card rounded-xl border border-border overflow-hidden transition-base hover:shadow-lg">
      <div className="p-4 md:p-5">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <button
            onClick={handleProfileClick}
            className="flex items-center gap-3 hover:opacity-80 transition-base"
          >
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden bg-muted flex-shrink-0">
              <Image
                src={post?.userAvatar}
                alt={post?.userAvatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground caption md:text-base">
                  {post?.username}
                </span>
                {post?.isVerified && (
                  <Icon name="BadgeCheck" size={16} color="var(--color-primary)" />
                )}
              </div>
              <span className="text-muted-foreground caption">
                {formatTimestamp(post?.timestamp)}
              </span>
            </div>
          </button>

          <button
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-base"
            aria-label="More options"
          >
            <Icon name="MoreHorizontal" size={20} />
          </button>
        </div>

        {post?.caption && (
          <p className="text-foreground mb-3 md:mb-4 leading-relaxed">
            {post?.caption}
          </p>
        )}

        {post?.hashtags && post?.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3 md:mb-4">
            {post?.hashtags?.map((tag, index) => (
              <span
                key={index}
                className="caption text-primary font-mono hover:underline cursor-pointer"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
      {post?.mediaType === 'image' && post?.mediaUrl && (
        <button
          onClick={handleImageClick}
          className="w-full aspect-square overflow-hidden bg-muted cursor-pointer"
        >
          <Image
            src={post?.mediaUrl}
            alt={post?.mediaAlt}
            className="w-full h-full object-cover hover:scale-105 transition-base duration-300"
          />
        </button>
      )}
      {post?.mediaType === 'video' && post?.mediaUrl && (
        <div className="w-full aspect-square overflow-hidden bg-muted relative">
          <video
            src={post?.mediaUrl}
            className="w-full h-full object-cover"
            controls
            playsInline
          />
        </div>
      )}
      <div className="p-4 md:p-5">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="flex items-center gap-4 md:gap-6">
            <button
              onClick={handleLikeClick}
              className="flex items-center gap-2 group transition-base"
              aria-label={isLiked ? 'Unlike post' : 'Like post'}
            >
              <div className={`transition-base ${isLiked ? 'scale-110' : 'group-hover:scale-110'}`}>
                <Icon
                  name="Heart"
                  size={24}
                  color={isLiked ? 'var(--color-error)' : 'currentColor'}
                  strokeWidth={isLiked ? 0 : 2}
                  className={isLiked ? 'fill-error' : 'group-hover:text-error'}
                />
              </div>
              <span className={`caption font-medium ${isLiked ? 'text-error' : 'text-foreground'}`}>
                {likeCount?.toLocaleString()}
              </span>
            </button>

            <button
              onClick={() => onComment(post?.id)}
              className="flex items-center gap-2 group transition-base hover:text-primary"
              aria-label="Comment on post"
            >
              <Icon name="MessageCircle" size={24} strokeWidth={2} />
              <span className="caption font-medium text-foreground group-hover:text-primary">
                {post?.comments?.toLocaleString()}
              </span>
            </button>

            <div className="relative">
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="flex items-center gap-2 group transition-base hover:text-primary"
                aria-label="Share post"
              >
                <Icon name="Share2" size={24} strokeWidth={2} />
                <span className="caption font-medium text-foreground group-hover:text-primary">
                  Share
                </span>
              </button>

              {showShareMenu && (
                <div className="absolute bottom-full left-0 mb-2 bg-card border border-border rounded-lg shadow-lg p-2 min-w-[160px] z-10">
                  <button
                    onClick={() => {
                      onShare(post?.id, 'copy');
                      setShowShareMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted transition-base text-left"
                  >
                    <Icon name="Copy" size={18} />
                    <span className="caption">Copy Link</span>
                  </button>
                  <button
                    onClick={() => {
                      onShare(post?.id, 'message');
                      setShowShareMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted transition-base text-left"
                  >
                    <Icon name="Send" size={18} />
                    <span className="caption">Send Message</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <button
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-base"
            aria-label="Save post"
          >
            <Icon name="Bookmark" size={24} strokeWidth={2} />
          </button>
        </div>

        {post?.topComments && post?.topComments?.length > 0 && (
          <div className="space-y-2">
            {post?.topComments?.slice(0, 2)?.map((comment, index) => (
              <div key={index} className="flex gap-2">
                <span className="font-medium caption text-foreground">
                  {comment?.username}
                </span>
                <span className="caption text-foreground line-clamp-1">
                  {comment?.text}
                </span>
              </div>
            ))}
            {post?.comments > 2 && (
              <button
                onClick={() => onOpenDetails(post)}
                className="caption text-muted-foreground hover:text-foreground transition-base"
              >
                View all {post?.comments} comments
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default PostCard;